import React, { useRef, useLayoutEffect, useState } from 'react';
import { CarWindowDto } from './GlassControl.types';
import { FixedElementBadge } from '../shared/FixedElementBadge';

interface WindowLabelProps {
  /** Dane szyby */
  data: CarWindowDto;
  /** Etykieta pozycji */
  label: string;
  /** Pozycja procentowa względem kontenera */
  position: { top: string; left: string };
  /** Czy etykieta jest aktywna/zaznaczona */
  isActive?: boolean;
  /** Czy pokazać wskaźnik/strzałkę kierunkową */
  showPointer?: boolean;
  /** Kierunek wskaźnika */
  pointerDirection?: 'top' | 'bottom' | 'left' | 'right' | 'bottom-left' | 'bottom-right' | 'top-left' | 'top-right';
  /** Czy etykieta jest podświetlona (hover na powiązanym SVG) */
  isHighlighted?: boolean;
  /** Callback na hover */
  onMouseEnter?: () => void;
  /** Callback na opuszczenie hovera */
  onMouseLeave?: () => void;
}

type GrowDirection = 'left' | 'right' | 'center';
type PointerDirection = 'top' | 'bottom' | 'left' | 'right' | 'bottom-left' | 'bottom-right' | 'top-left' | 'top-right';

/**
 * Określa kierunek rozszerzania elementu na podstawie pointerDirection
 * Etykieta rośnie w kierunku PRZECIWNYM do pozycji pointera na osi X:
 * - pointer po lewej (np. bottom-left, top-left) → etykieta rośnie w prawo
 * - pointer po prawej (np. bottom-right, top-right) → etykieta rośnie w lewo
 * - pointer na środku (top, bottom, left, right) → etykieta rośnie symetrycznie
 */
function getGrowDirectionFromPointer(pointerDirection: PointerDirection): GrowDirection {
  if (pointerDirection.endsWith('-left')) return 'right';   // Pointer po lewej - etykieta rośnie w prawo
  if (pointerDirection.endsWith('-right')) return 'left';   // Pointer po prawej - etykieta rośnie w lewo
  return 'center';                                           // Pointer na środku - symetrycznie
}

/**
 * Oblicza przesunięcie X na podstawie kierunku rozszerzania i szerokości elementu
 * Punkt kotwiczący (gdzie jest pointer) pozostaje w miejscu określonym przez position.left
 */
function calculateTranslateX(growDirection: GrowDirection, width: number): number {
  switch (growDirection) {
    case 'right':
      return 0;              // Punkt kotwiczący po lewej - bez przesunięcia
    case 'left':
      return -width;         // Punkt kotwiczący po prawej - przesuń o całą szerokość w lewo
    case 'center':
    default:
      return -width / 2;     // Punkt kotwiczący na środku - przesuń o połowę
  }
}

export function WindowLabel({
  data,
  label,
  position,
  isActive = false,
  showPointer = false,
  pointerDirection = 'bottom',
  isHighlighted = false,
  onMouseEnter,
  onMouseLeave,
}: WindowLabelProps) {
  const { manufacturer, dateCode, absence } = data;
  const containerRef = useRef<HTMLDivElement>(null);
  const [translateX, setTranslateX] = useState(0);

  // Określ kierunek rozszerzania na podstawie pointerDirection
  const growDirection = getGrowDirectionFromPointer(pointerDirection);

  // Oblicz przesunięcie na podstawie rzeczywistej szerokości elementu
  useLayoutEffect(() => {
    if (containerRef.current) {
      const width = containerRef.current.offsetWidth;
      setTranslateX(calculateTranslateX(growDirection, width));
    }
  }, [growDirection, manufacturer, dateCode, label]);

  // Renderuj wskaźnik kierunkowy - linia z kropką
  const renderPointer = () => {
    if (!showPointer) return null;

    const pointerConfig: Record<
      string,
      {
        lineStyle: React.CSSProperties;
        circleStyle: React.CSSProperties;
      }
    > = {
      // Dla 'top' i 'bottom' pointer jest na środku etykiety
      top: {
        lineStyle: {
          top: '-32px',
          left: '50%',
          transform: 'translateX(-50%)',
          width: '2px',
          height: '32px',
        },
        circleStyle: {
          top: '-40px',
          left: '50%',
          transform: 'translateX(-50%)',
        },
      },
      bottom: {
        lineStyle: {
          bottom: '-30px',
          left: '50%',
          transform: 'translateX(-50%)',
          width: '2px',
          height: '32px',
        },
        circleStyle: {
          bottom: '-38px',
          left: '50%',
          transform: 'translateX(-50%)',
        },
      },
      left: {
        lineStyle: {
          left: '-40px',
          top: '50%',
          transform: 'translateY(-50%)',
          width: '32px',
          height: '2px',
        },
        circleStyle: {
          left: '-40px',
          top: '50%',
          transform: 'translateY(-50%)',
        },
      },
      right: {
        lineStyle: {
          right: '-33px',
          top: '50%',
          transform: 'translateY(-50%)',
          width: '32px',
          height: '2px',
        },
        circleStyle: {
          right: '-40px',
          top: '50%',
          transform: 'translateY(-50%)',
        },
      },
      // Dla kierunków złożonych pointer jest na krawędzi etykiety (punkt kotwiczący)
      // -left: pointer na lewej krawędzi (0%), etykieta rośnie w prawo
      // -right: pointer na prawej krawędzi (100%), etykieta rośnie w lewo
      'bottom-left': {
        lineStyle: {
          bottom: '-30px',
          left: '30px',
          transform: 'translateX(-50%)',
          width: '2px',
          height: '32px',
        },
        circleStyle: {
          bottom: '-38px',
          left: '30px',
          transform: 'translateX(-50%)',
        },
      },
      'bottom-right': {
        lineStyle: {
          bottom: '-30px',
          right: '30px',
          transform: 'translateX(50%)',
          width: '2px',
          height: '32px',
        },
        circleStyle: {
          bottom: '-38px',
          right: '30px',
          transform: 'translateX(50%)',
        },
      },
      'top-left': {
        lineStyle: {
          top: '-30px',
          left: '30px',
          transform: 'translateX(-50%)',
          width: '2px',
          height: '32px',
        },
        circleStyle: {
          top: '-38px',
          left: '30px',
          transform: 'translateX(-50%)',
        },
      },
      'top-right': {
        lineStyle: {
          top: '-30px',
          right: '30px',
          transform: 'translateX(50%)',
          width: '2px',
          height: '32px',
        },
        circleStyle: {
          top: '-38px',
          right: '30px',
          transform: 'translateX(50%)',
        },
      },
    };

    const { lineStyle, circleStyle } = pointerConfig[pointerDirection];

    return (
      <>
        {/* Linia */}
        <div
          className="absolute bg-navy-900"
          style={lineStyle}
        />
        {/* Kropka na końcu */}
        <div
          className="absolute w-2 h-2 rounded-full bg-navy-900"
          style={circleStyle}
        />
      </>
    );
  };

  const highlightStyle = isHighlighted
    ? 'ring-2 ring-[#0064FF] bg-navy-700'
    : '';

  // Jeśli brak szyby, wyświetl prostszą wersję
  if (absence) {
    return (
      <div
        ref={containerRef}
        className="absolute cursor-pointer"
        style={{
          left: position.left,
          top: position.top,
          transform: `translateX(${translateX}px)`,
          transition: 'transform 0.2s',
        }}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        <div className={`flex items-center rounded-[15px] px-5 py-3 transition-all duration-200 ${isHighlighted ? highlightStyle : 'bg-navy-900'}`}>
          <div className="flex flex-col gap-1.5 leading-none whitespace-nowrap">
            <span className="text-[12px] font-medium text-blue-300 tracking-[0.3px] font-archivo">
              {label}
            </span>
            <span className="text-[14px] font-semibold text-white tracking-[0.5px]">
              Brak
            </span>
          </div>
        </div>
        {renderPointer()}
      </div>
    );
  }

  // Standardowa etykieta z datą i producentem
  return (
    <div
      ref={containerRef}
      className="absolute cursor-pointer group"
      style={{
        left: position.left,
        top: position.top,
        transform: `translateX(${translateX}px)`,
        transition: 'transform 0.2s',
      }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div
        className={`flex items-center rounded-[15px] transition-all duration-200 ${
          isHighlighted
            ? highlightStyle
            : isActive ? 'bg-navy-700' : 'bg-navy-900'
          }`}
      >
        {/* Data produkcji - tylko jeśli jest dateCode */}
        {dateCode && (
          <>
            <div className="flex justify-center items-center px-3 py-3">
              <span className="text-[13px] font-medium text-blue-300 leading-[14px] text-center whitespace-nowrap font-archivo">
                {dateCode}
              </span>
            </div>

            {/* Separator */}
            <div
              className={`h-full w-px ${isActive ? 'bg-navy-600' : 'bg-navy-800'
                }`}
            />
          </>
        )}

        {/* Informacje o szybie */}
        <div className="flex flex-col gap-1.5 px-5 py-3 leading-none whitespace-nowrap">
          <span className="text-[12px] font-medium text-blue-300 tracking-[0.3px] font-archivo">
            {label}
          </span>
          <span className="text-[14px] font-semibold text-white tracking-[0.5px]">
            {manufacturer || 'Brak danych'}
          </span>
        </div>
      </div>

      {/* Ikona naprawy z tooltipem - jeśli szyba była wymieniana */}
      {data.isReplaced && (
        <div className="absolute left-3 -top-4 z-20">
          <FixedElementBadge disclaimerText="Element wymieniany" />
        </div>
      )}

      {/* Wskaźnik kierunkowy */}
      {renderPointer()}
    </div>
  );
}
